import {
  type CreateLockerverseSignupOptions,
  createLockerverseSignupClient,
  type LockerverseSdkError,
  type LockerverseSignup,
  type LockerverseSignupClient,
} from "@lockerverse/sdk/signup";
import { useCallback, useEffect, useRef, useState } from "react";
import { useImmutableResourceIdentity } from "./immutable-resource-identity.ts";

type SignupLoadState = {
  error: LockerverseSdkError | null;
  loading: boolean;
  signup: LockerverseSignup | null;
};

export type UseLockerverseSignupOptions = {
  /** Initial public SDK snapshot. The hook still refreshes on mount. */
  initialSignup?: LockerverseSignup;
};

export type UseLockerverseSignupResult = {
  client: LockerverseSignupClient;
  error: LockerverseSdkError | null;
  loading: boolean;
  refresh: () => void;
  signup: LockerverseSignup | null;
};

export function useLockerverseSignup(
  options: CreateLockerverseSignupOptions,
  { initialSignup }: UseLockerverseSignupOptions = {}
): UseLockerverseSignupResult {
  const { onError } = options;
  useImmutableResourceIdentity("signup", {
    apiBaseUrl: options.apiBaseUrl,
    communitySlug: options.communitySlug,
    environment: options.environment,
    slug: options.signupSlug,
  });
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const [client] = useState(() => {
    const clientOptions: CreateLockerverseSignupOptions = {
      ...options,
      onError: (error) => onErrorRef.current?.(error),
    };
    return createLockerverseSignupClient(clientOptions);
  });
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<SignupLoadState>(() => ({
    error: null,
    loading: true,
    signup: initialSignup ?? null,
  }));

  useEffect(() => {
    let active = true;
    client.load().then(
      (signup) => {
        if (active) {
          setState({
            error: null,
            loading: false,
            signup,
          });
        }
      },
      (error: LockerverseSdkError) => {
        if (active) {
          setState((current) => ({ ...current, error, loading: false }));
        }
      }
    );
    return () => {
      active = false;
    };
  }, [client, revision]);

  const refresh = useCallback(() => {
    setState((current) => ({ ...current, error: null, loading: true }));
    setRevision((value) => value + 1);
  }, []);

  return {
    client,
    error: state.error,
    loading: state.loading,
    refresh,
    signup: state.signup,
  };
}
